import { __decorate } from "tslib";
import _ from 'lodash';
import { Toast } from 'patsnap-biz';
import { provide, injectService } from '@/inversify.config';
import { getFolderApi } from '@cores/apis/workspace_api/folder';
import { postAutoCollectPreviewApi, postAutoCollectUpdateApi } from '@cores/apis/workspace_api/patents';
import { getDatabaseConfig } from '@cores/apis/others_api/core_basic';
import { SEARCH_MODE } from '@/types/presentation/components/AutomaticUpdate.type';
import { formatNumberByKilo } from '@cores/utils/formatNumber';
import { rvTrack } from '@cores/services/tracker/bi';
import { DetailServiceSymbol } from '@/services/DetailService';
import { UserService } from '@/domains/user/services/user-service.interface';
export const AutomaticUpdateServiceSymbol = Symbol.for('AutomaticUpdateService');
var UPDATE_TYPE;
(function (UPDATE_TYPE) {
    UPDATE_TYPE["ALL"] = "ALL";
    UPDATE_TYPE["NEW"] = "NEW";
})(UPDATE_TYPE || (UPDATE_TYPE = {}));
var OPERATE_TYPE;
(function (OPERATE_TYPE) {
    OPERATE_TYPE["AND"] = "AND";
    OPERATE_TYPE["OR"] = "OR";
    OPERATE_TYPE["NOT"] = "NOT";
})(OPERATE_TYPE || (OPERATE_TYPE = {}));
var SWITCH_TYPE;
(function (SWITCH_TYPE) {
    SWITCH_TYPE["APNO"] = "APNO";
})(SWITCH_TYPE || (SWITCH_TYPE = {}));
const defaultAutoQueryData = {
    switch: false,
    q: '',
    efq: '',
    stem: true,
    family_mode: '',
    update_type: UPDATE_TYPE.ALL,
    update_dbs: {
        patentTypes: [],
        patentCountries: [],
    },
    include_history: true,
};
const defaultAutoCalculationData = {
    switch: false,
    cal_folder_ids: [],
    cal_merge_type: OPERATE_TYPE.AND,
    folder_auto_update: true,
    clear_old: false,
};
const defaultAutoFamilyModeData = {
    switch: false,
    expansion_family: SWITCH_TYPE.APNO,
};
const defaultErrorData = {
    errorCode: '',
    errorMsg: '',
};
const defaultData = {
    isPreviewLoading: false,
    previewCount: 0,
    errorData: _.cloneDeep(defaultErrorData),
};
let AutomaticUpdateService = class AutomaticUpdateService {
    constructor() {
        // isOpen = false;
        this.spaceId = '';
        this.folderId = '';
        this.folderInfo = null;
        this.configDbs = null; // 全量db配置数据
        this.originQueryDataString = ''; // 记录原始的query数据
        this.autoQuery = {
            isPreviewLoading: false,
            previewCount: 0,
            data: _.cloneDeep(defaultAutoQueryData),
            errorData: _.cloneDeep(defaultErrorData),
        };
        this.autoCalculation = {
            isPreviewLoading: false,
            previewCount: 0,
            data: _.cloneDeep(defaultAutoCalculationData),
            errorData: _.cloneDeep(defaultErrorData),
        };
        this.autoFamilyMode = {
            isPreviewLoading: false,
            previewCount: 0,
            data: _.cloneDeep(defaultAutoFamilyModeData),
            errorData: _.cloneDeep(defaultErrorData),
        };
        this.autoPreviewData = {};
    }
    get totalPreviewData() {
        let totalCount = 0;
        if (this.autoQuery.data.switch && this.autoQuery.previewCount && this.autoQuery.data.update_type !== UPDATE_TYPE.NEW) {
            totalCount += this.autoQuery.previewCount;
        }
        if (this.autoCalculation.data.switch && this.autoCalculation.previewCount) {
            totalCount += this.autoCalculation.previewCount;
        }
        if (this.autoFamilyMode.data.switch && this.autoFamilyMode.previewCount) {
            totalCount += this.autoFamilyMode.previewCount;
        }
        const formateTotalCount = formatNumberByKilo(totalCount);
        let overCapacityType = '';
        if (totalCount > 0) {
            if (totalCount > this.autoPreviewData.folder_capacity) {
                overCapacityType = 'over_capacity';
            }
            else if (totalCount + this.autoPreviewData.folder_patent_count > this.autoPreviewData.folder_capacity) {
                overCapacityType = 'folder_capacity';
            }
            else if (totalCount + this.autoPreviewData.saved_patent_count > this.autoPreviewData.total_capacity) {
                overCapacityType = 'total_capacity';
            }
        }
        return {
            formateTotalCount,
            isOverCapacity: Boolean(overCapacityType),
            overCapacityType: overCapacityType,
        };
    }
    get canSubmit() {
        const canSubmit = !((_.get(this.autoQuery, 'data.switch') && (!_.get(this.autoQuery, 'data.q') || _.get(this.autoQuery, 'errorData.errorCode'))) ||
            (_.get(this.autoCalculation, 'data.switch') && ((_.get(this.autoCalculation, 'data.cal_folder_ids.length') < 2) || _.get(this.autoCalculation, 'errorData.errorCode'))) ||
            (_.get(this.autoFamilyMode, 'data.switch') && _.get(this.autoFamilyMode, 'errorData.errorCode')));
        return canSubmit;
    }
    get isDataChanged() {
        const currentQueryDataString = this.getStringify({
            autoQueryData: this.autoQuery.data,
            autoCalculationData: this.autoCalculation.data,
            autoFamilyModeData: this.autoFamilyMode.data,
        });
        return this.originQueryDataString !== currentQueryDataString;
    }
    get hasSwitchOn() {
        return this.autoQuery.data.switch || this.autoCalculation.data.switch || this.autoFamilyMode.data.switch;
    }
    init(opts) {
        const detailService = injectService(DetailServiceSymbol);
        const { spaceId, folderId, } = detailService;
        this.spaceId = _.get(opts, 'space_id') || spaceId;
        this.folderId = _.get(opts, 'folder_id') || folderId;
        // this.isOpen = true;
        this.folderInfo = null;
        this.getFolderInfo();
        this.getDatabaseConfig();
        this.initAutoUpdateData();
    }
    initAutoUpdateData() {
        this.autoQuery = this.initAutoQuery();
        this.autoCalculation = this.initCalculation();
        this.autoFamilyMode = this.initFamilyMode();
        this.originQueryDataString = this.getStringify({
            autoQueryData: this.autoQuery.data,
            autoCalculationData: this.autoCalculation.data,
            autoFamilyModeData: this.autoFamilyMode.data,
        });
    }
    initAutoQuery() {
        if (!this.folderInfo) {
            return {
                data: {
                    ...defaultAutoQueryData,
                },
                ...defaultData,
            };
        }
        const { auto_query_on_off, q, efq, update_stem, update_dbs, update_type, family_search, filter_public_day, } = this.folderInfo;
        const userService = injectService(UserService);
        return {
            data: {
                ...defaultAutoQueryData,
                include_history: !filter_public_day,
                switch: auto_query_on_off,
                q: q,
                efq: efq,
                stem: update_stem,
                family_mode: family_search && userService.userPermission.family_search ? SEARCH_MODE.FAMILY : SEARCH_MODE.PATENT,
                update_type,
                update_dbs: update_dbs || {
                    patentCountries: [],
                    patentTypes: [],
                },
            },
            ...defaultData,
        };
    }
    initCalculation() {
        if (!this.folderInfo) {
            return {
                data: {
                    ...defaultAutoCalculationData,
                },
                ...defaultData,
            };
        }
        const { auto_calculate_on_off, cal_merge_type, cal_folder_infos, } = this.folderInfo;
        return {
            data: {
                ...defaultAutoCalculationData,
                switch: auto_calculate_on_off,
                cal_folder_ids: _.map(cal_folder_infos, (folder) => folder.folder_id),
                cal_merge_type: (cal_merge_type || OPERATE_TYPE.AND),
            },
            ...defaultData,
        };
    }
    initFamilyMode() {
        if (!this.folderInfo) {
            return {
                data: {
                    ...defaultAutoFamilyModeData,
                },
                ...defaultData,
            };
        }
        const { auto_expand_on_off, expansion_family, } = this.folderInfo;
        return {
            data: {
                ...defaultAutoFamilyModeData,
                switch: auto_expand_on_off,
                expansion_family: expansion_family || SWITCH_TYPE.APNO,
            },
            ...defaultData,
        };
    }
    getStringify({ autoQueryData, autoCalculationData, autoFamilyModeData }) {
        return JSON.stringify({
            autoQueryData,
            autoCalculationData,
            autoFamilyModeData,
        });
    }
    // close() {
    //     this.isOpen = false;
    //     this.spaceId = '';
    //     this.folderId = '';
    // }
    getFolderInfo() {
        return getFolderApi({
            space_id: this.spaceId,
            folder_id: this.folderId,
        }).then((data) => {
            this.folderInfo = data;
            this.initAutoUpdateData();
        }).catch((error) => {
            error._error_msg &&
                Toast({
                    type: 'error',
                    message: error._error_msg,
                    showClose: true,
                });
        });
    }
    getDatabaseConfig() {
        if (this.configDbs) {
            return Promise.resolve(this.configDbs);
        }
        return getDatabaseConfig().then((data) => {
            this.configDbs = data;
            return data;
        });
    }
    // 更新query模式下的数据
    updateAutoQueryData(data) {
        this.autoQuery.data = {
            ...this.autoQuery.data,
            switch: true,
            ...data,
        };
        this.autoQuery.errorData = {};
        if (!this.autoQuery.data.q) {
            this.autoQuery.previewCount = 0;
            return;
        }
        this.autoQuery.isPreviewLoading = true;
        this.getQueryPreviewCount().then(({ data }) => {
            // 如果开启仅收录未来的专利，仍然需要调用接口（检测query的合法性），但是preview_count为0
            if (this.autoQuery.data.update_type === UPDATE_TYPE.NEW) {
                this.autoQuery.previewCount = 0;
            }
            else {
                this.autoQuery.previewCount = data.preview_count;
                this.autoPreviewData = data;
            }
        }).catch(err => {
            this.autoQuery.previewCount = 0;
            this.autoQuery.errorData = {
                errorCode: err._error_code,
                errorMsg: err._error_msg,
            };
        }).finally(() => {
            this.autoQuery.isPreviewLoading = false;
        });
    }
    updateAutoQueryUpdateType(type) {
        this.autoQuery.data.update_type = type;
        const data = this.autoQuery.data;
        if (data.update_type === UPDATE_TYPE.ALL &&
            data.switch &&
            data.q) {
            this.updateAutoQueryData(data);
        }
        if (data.update_type === UPDATE_TYPE.NEW) {
            this.autoQuery.previewCount = 0;
            this.autoQuery.errorData = {};
        }
    }
    getQueryPreviewCount() {
        const autoPreviewPatentParams = {
            auto_query_request: {
                on_off: this.autoQuery.data.switch,
                q: this.autoQuery.data.q,
                efq: this.autoQuery.data.efq,
                valid_dbs: {
                    patent_countries: this.autoQuery.data.update_dbs.patentCountries,
                    patent_types: this.autoQuery.data.update_dbs.patentTypes,
                },
                stem_on: this.autoQuery.data.stem,
                family_search: this.autoQuery.data.family_mode === SEARCH_MODE.FAMILY,
                filter_public_day: !this.autoQuery.data.include_history,
            },
        };
        return postAutoCollectPreviewApi(this.folderId, autoPreviewPatentParams);
    }
    // 更新calculation模式下的数据
    updateAutoCalculationData(data) {
        this.autoCalculation.data = {
            ...this.autoCalculation.data,
            switch: true,
            ...data,
        };
        this.autoCalculation.errorData = {};
        if (this.autoCalculation.data.cal_folder_ids.length < 2) {
            this.autoCalculation.previewCount = 0;
            return;
        }
        this.autoCalculation.isPreviewLoading = true;
        this.getCalculationPreviewCount().then(({ data }) => {
            this.autoCalculation.previewCount = data.preview_count;
            this.autoPreviewData = data;
        }).catch(err => {
            this.autoCalculation.previewCount = 0;
            this.autoCalculation.errorData = {
                errorCode: err._error_code,
                errorMsg: err._error_msg,
            };
        }).finally(() => {
            this.autoCalculation.isPreviewLoading = false;
        });
    }
    updateAutoCalculationErrorData({ errorCode, errorMsg }) {
        this.autoCalculation.errorData = {
            errorCode,
            errorMsg,
        };
    }
    getCalculationPreviewCount() {
        const autoPreviewPatentParams = {
            auto_calculate_request: {
                on_off: this.autoCalculation.data.switch,
                cal_folder_ids: this.autoCalculation.data.cal_folder_ids,
                cal_merge_type: this.autoCalculation.data.cal_merge_type,
            },
        };
        return postAutoCollectPreviewApi(this.folderId, autoPreviewPatentParams);
    }
    // 更新family模式下的数据
    updateAutoFamilyModeData(data) {
        this.autoFamilyMode.data = {
            ...this.autoFamilyMode.data,
            switch: true,
            ...data,
        };
        this.autoFamilyMode.errorData = {};
        this.autoFamilyMode.isPreviewLoading = true;
        this.getFamilyModePreviewCount().then(({ data }) => {
            this.autoFamilyMode.previewCount = data.preview_count;
            this.autoPreviewData = data;
        }).catch(err => {
            this.autoFamilyMode.previewCount = 0;
            this.autoFamilyMode.errorData = {
                errorCode: err._error_code,
                errorMsg: err._error_msg,
            };
        }).finally(() => {
            this.autoFamilyMode.isPreviewLoading = false;
        });
    }
    getFamilyModePreviewCount() {
        const autoPreviewPatentParams = {
            auto_expand_request: {
                on_off: this.autoCalculation.data.switch,
                expansion_family: this.autoFamilyMode.data.expansion_family,
            },
        };
        return postAutoCollectPreviewApi(this.folderId, autoPreviewPatentParams);
    }
    // 更新switch状态
    updateSwitchStatus(switchType, status) {
        switch (switchType) {
            case 'QUERY_FOLDER':
                if (!status) {
                    this.autoQuery = this.initAutoQuery();
                }
                else {
                    this.updateAutoQueryData(this.autoQuery.data);
                }
                this.autoQuery.data.switch = status;
                break;
            case 'CALCULATE_FOLDER':
                if (!status) {
                    this.autoCalculation = this.initCalculation();
                }
                else {
                    this.updateAutoCalculationData(this.autoCalculation.data);
                }
                this.autoCalculation.data.switch = status;
                break;
            case 'FAMILY_EXTEND':
                if (!status) {
                    this.autoFamilyMode = this.initFamilyMode();
                }
                else {
                    this.updateAutoFamilyModeData(this.autoFamilyMode.data);
                }
                this.autoFamilyMode.data.switch = status;
                break;
            default:
                break;
        }
    }
    // 更新folder自动收录参数
    updateAutoCollectParams(data) {
        return postAutoCollectUpdateApi(this.folderId, {
            clear_old: data.clear_old,
            auto_query_request: {
                on_off: this.autoQuery.data.switch,
                q: this.autoQuery.data.q,
                efq: this.autoQuery.data.efq,
                valid_dbs: {
                    patent_countries: this.autoQuery.data.update_dbs.patentCountries,
                    patent_types: this.autoQuery.data.update_dbs.patentTypes,
                },
                stem_on: this.autoQuery.data.stem,
                family_search: this.autoQuery.data.family_mode === SEARCH_MODE.FAMILY,
                update_type: data.update_type,
                filter_public_day: !this.autoQuery.data.include_history,
            },
            auto_calculate_request: {
                on_off: this.autoCalculation.data.switch,
                cal_folder_ids: this.autoCalculation.data.cal_folder_ids,
                cal_merge_type: this.autoCalculation.data.cal_merge_type,
            },
            auto_expand_request: {
                on_off: this.autoFamilyMode.data.switch,
                expansion_family: this.autoFamilyMode.data.expansion_family,
            },
        }).then(res => {
            this.folderInfo = res.data;
            this.initAutoUpdateData();
            return res;
        }).finally(() => {
            rvTrack('Submit Automatic Update Dialog', {
                other_param: data.clear_old,
                other_param1: data.update_type,
                other_param2: this.autoQuery.data.switch,
                other_param3: this.autoCalculation.data.switch,
                other_param4: this.autoFamilyMode.data.switch,
            });
        });
    }
};
AutomaticUpdateService = __decorate([
    provide(AutomaticUpdateServiceSymbol)
], AutomaticUpdateService);
export { AutomaticUpdateService };
